import { ChangeDetectorRef, Component, Input, inject, output, OutputEmitterRef } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { AutocompleteDataSource } from '@app/shared/ui/components/inputs/autocomplete/autocomplete.helper';
import { AutocompleteVersion } from '@app/shared/enums/general.enums';
import { mapEntityForAutocomplete } from '@app/pages/new-matter/helper/new-matter.helper';
import { TranslateService } from '@ngx-translate/core';
import { mapEntitiesToAutocompleteOptions } from '@app/services/helpers/management.helper';
import { DropDownOption } from '@core';

@Component({
	selector: 'employees-search--form',
	templateUrl: './employees-search-form.component.html',
	styleUrls: ['./employees-search-form.component.scss'],
	standalone: false
})
export class EmployeesSearchFormComponent {
	private cdr = inject(ChangeDetectorRef);
	private translateService = inject(TranslateService);

	@Input() set formData(val: Record<string, any>) {
		if (val) {
			this.fillFormWithInitialData(val);
		}
	}
	readonly submit: OutputEmitterRef<Record<string, any>> = output<Record<string, any>>();

	public readonly autocompleteDataSource: typeof AutocompleteDataSource = AutocompleteDataSource;
	public readonly autocompleteVersion: typeof AutocompleteVersion = AutocompleteVersion;
	public readonly statusOptions: DropDownOption[] = [
		{ label: 'DRAWERS.CREATION.CONTROLS.EMPLOYEE_STATUS.OPTIONS.ACTIVE', value: true },
		{ label: 'DRAWERS.CREATION.CONTROLS.EMPLOYEE_STATUS.OPTIONS.INACTIVE', value: false }
	];

	public readonly STATUS_CONTROL_NAME: string = 'isActive';
	public readonly BRANCH_CONTROL_NAME: string = 'branch';
	public readonly DEPARTMENT_CONTROL_NAME: string = 'department';
	public readonly NAME_CONTROL_NAME: string = 'employeeName';
	public readonly ROLE_CONTROL_NAME: string = 'role';
	public readonly EMAIL_CONTROL_NAME: string = 'email';
	public readonly PHONE_CONTROL_NAME: string = 'phoneNumber';

	public formGroup: UntypedFormGroup = new UntypedFormGroup({
		[this.STATUS_CONTROL_NAME]: new UntypedFormControl(),
		[this.BRANCH_CONTROL_NAME]: new UntypedFormControl(),
		[this.DEPARTMENT_CONTROL_NAME]: new UntypedFormControl(),
		[this.NAME_CONTROL_NAME]: new UntypedFormControl(),
		[this.ROLE_CONTROL_NAME]: new UntypedFormControl(),
		[this.EMAIL_CONTROL_NAME]: new UntypedFormControl(),
		[this.PHONE_CONTROL_NAME]: new UntypedFormControl()
	});

	private fillFormWithInitialData(data: Record<string, any>): void {
		Object.keys(data).forEach((key: string) => {
			if (this.formGroup.contains(key)) {
				if (key === 'department' || key === 'role') {
					data[key] = mapEntitiesToAutocompleteOptions(data[key], this.translateService.currentLang, 'name');
				}
				if (key === 'role') {
					data[key] = [mapEntityForAutocomplete(data[key][0], this.translateService.currentLang, 'name')];
				}
				if (key === 'isActive') {
					data[key] = data[key] === 'true';
				}
				this.formGroup.get(key).setValue(data[key]);
			}
		});
	}

	private submitForm(): void {
		this.submit.emit(this.formGroup.value);
	}

	public onClearAll(): void {
		this.formGroup.reset();
	}

	public onSubmit(): void {
		if (this.formGroup.valid) {
			this.submitForm();
		} else {
			this.formGroup.markAllAsTouched();
			this.cdr.markForCheck();
		}
	}
}
